/**
 * WalletDocuments — My Financial Documents.
 *
 * Central repository for existing/issued financial products, policies
 * and contracts. Strict boundary vs My Decisions (which holds proposals
 * currently being considered).
 *
 * Landing page: NAVI insight card → filter chips → table.
 * Detail: inline drawer with Overview · Key info · Ask NAVI · Explain.
 * Upload: 2-step drawer — pick file → extraction confirmation → save.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FileText, Search, Plus, Filter, ArrowRight, Upload, CheckCircle2,
  Sparkles, X, ShieldCheck, Info, Building2, ExternalLink, HelpCircle,
  MessageSquare, Loader2, Edit3, ArrowLeft,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { loadWallet } from "@/lib/wallet-storage";
import NaviFace from "@/components/wallet/NaviFace";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const FILTERS = ["All", "Insurance", "Investments", "Retirement", "Credit", "Banking", "Estate Planning", "Healthcare", "Other"];

export default function WalletDocuments() {
  const wallet = loadWallet() || {};
  const deviceId = wallet.device_id;
  const [items, setItems]     = useState([]);
  const [landscape, setLandscape] = useState(null);
  const [filter, setFilter]   = useState("All");
  const [q, setQ]             = useState("");
  const [loading, setLoading] = useState(true);
  const [openDoc, setOpenDoc] = useState(null);   // doc_id being viewed
  const [uploadOpen, setUploadOpen] = useState(false);

  async function refresh() {
    if (!deviceId) { setLoading(false); return; }
    setLoading(true);
    const [{ data: list }, { data: land }] = await Promise.all([
      axios.get(`${API}/wallet/financial-documents`, { params: { device_id: deviceId, filter: filter === "All" ? undefined : filter, q: q || undefined } }),
      axios.get(`${API}/wallet/financial-documents/insights/landscape`, { params: { device_id: deviceId } }),
    ]);
    setItems(list.items || []);
    setLandscape(land);
    setLoading(false);
  }
  useEffect(() => { refresh(); /* eslint-disable-line */ }, [filter, q]);

  return (
    <WalletShell activeTab="documents">
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="wallet-financial-documents">
        <header>
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>My Financial Documents</div>
          <h1 className="font-display text-2xl lg:text-3xl font-semibold mt-1" style={{ color: NAVY }}>
            Your important financial records — in one place
          </h1>
          <p className="text-[13px] text-slate-600 mt-2 max-w-2xl leading-relaxed">
            Keep your important financial documents together and understand what they mean.
          </p>
        </header>

        <NaviInsightCard landscape={landscape} />

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="documents-toolbar">
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f.toLowerCase().replace(/\s/g,"-")}`}
                className="rounded-full px-3.5 py-1.5 text-[11.5px] font-semibold transition"
                style={{
                  background: filter === f ? NAVY : "white",
                  color:      filter === f ? "white" : NAVY,
                  border:     `1px solid ${filter === f ? NAVY : "#E2E8F0"}`,
                }}
              >{f}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5">
              <Search className="w-3.5 h-3.5 text-slate-400" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search my documents"
                data-testid="documents-search"
                className="text-[12px] px-2 focus:outline-none w-48"
                style={{ color: NAVY }}
              />
            </div>
            <button
              onClick={() => setUploadOpen(true)}
              data-testid="documents-add"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-semibold text-white"
              style={{ background: NAVY }}
            >
              <Plus className="w-3.5 h-3.5" /> Add document
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center text-slate-400 animate-pulse">Loading documents…</div>
        ) : items.length === 0 ? (
          <EmptyState onAdd={() => setUploadOpen(true)} />
        ) : (
          <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden" data-testid="documents-table">
            <table className="w-full text-[13px]">
              <thead className="text-[10.5px] uppercase tracking-widest text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3">Document / policy</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Issued by</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => (
                  <tr key={d.financial_document_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`doc-row-${d.financial_document_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 flex-shrink-0" style={{ color: NAVY }} />
                        <div>
                          <div className="font-semibold" style={{ color: NAVY }}>{d.document_name}</div>
                          <div className="text-[10.5px] text-slate-500">{d.document_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{d.category_label}</td>
                    <td className="px-4 py-3">
                      <span style={{ color: NAVY }}>{d.issuer_name || "—"}</span>
                      {d.institution_id && <span className="ml-1 inline-flex items-center" title="Linked to a participating institution"><ShieldCheck className="w-3 h-3" style={{ color: "#059669" }} /></span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-[11.5px]">{formatDate(d.issue_date)}</td>
                    <td className="px-4 py-3"><StatusPill status={d.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setOpenDoc(d.financial_document_id)} data-testid={`doc-view-${d.financial_document_id}`} className="text-[12px] font-semibold inline-flex items-center gap-1" style={{ color: NAVY }}>
                        View <ArrowRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <BoundaryNote />

        <DemoIssueBar deviceId={deviceId} onIssued={refresh} />
      </div>

      {openDoc && <DocumentDetailDrawer docId={openDoc} deviceId={deviceId} onClose={() => setOpenDoc(null)} />}
      {uploadOpen && <UploadDrawer deviceId={deviceId} onClose={() => setUploadOpen(false)} onSaved={() => { setUploadOpen(false); refresh(); }} />}
    </WalletShell>
  );
}

/* ─────────────────── NAVI insight ─────────────────── */
function NaviInsightCard({ landscape }) {
  if (!landscape) return null;
  const cats = landscape.summary.filter(s => s.count > 0);
  const gap  = landscape.gaps?.[0];
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-4 lg:p-5 flex items-start gap-3" data-testid="navi-insight">
      <NaviFace size="md" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
          You have {landscape.total_documents} financial document{landscape.total_documents === 1 ? "" : "s"} on file.
        </div>
        {cats.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {cats.map(s => (
              <span key={s.filter} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold" style={{ background: `${GOLD}22`, color: NAVY }}>
                {s.count} {s.filter.toLowerCase()}
              </span>
            ))}
          </div>
        )}
        {gap && (
          <div className="mt-3 rounded-xl p-3" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }} data-testid="navi-gap">
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#B45309" }}>Something you may want to check</div>
            <div className="text-[13px] mt-1 leading-relaxed" style={{ color: NAVY }}>{gap.message}</div>
            <div className="text-[10.5px] text-slate-500 mt-1">{gap.boundary}</div>
          </div>
        )}
      </div>
    </section>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center">
      <FileText className="w-8 h-8 mx-auto text-slate-400" />
      <div className="mt-3 font-display text-[15px] font-semibold" style={{ color: NAVY }}>No documents yet</div>
      <div className="text-[12px] text-slate-500 mt-1">Add your first financial document and NAVI will help organise it.</div>
      <button onClick={onAdd} className="mt-4 inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-[13px] font-semibold text-white" style={{ background: NAVY }}>
        <Plus className="w-3.5 h-3.5" /> Add document
      </button>
    </div>
  );
}

function BoundaryNote() {
  return (
    <div className="rounded-xl p-3 flex items-start gap-2 text-[11px]" style={{ background: "#F1F5F9" }}>
      <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
      <div className="text-slate-600 leading-relaxed">
        My Financial Documents holds records of financial products you already have. Financial propositions you're currently considering live in <Link to="/wallet/decisions" className="font-semibold underline" style={{ color: NAVY }}>My Decisions</Link>. Altara records and explains — it does not give financial advice.
      </div>
    </div>
  );
}

function DemoIssueBar({ deviceId, onIssued }) {
  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState(null);
  async function trigger() {
    if (!deviceId || busy) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await axios.post(`${API}/admin/institution-issued-document/demo-issue`, { device_id: deviceId });
      setMsg(data.status === "already_issued"
        ? "ABC Wealth has already delivered this policy — no duplicate created."
        : "ABC Wealth delivered the final policy through Altara. It's now in your repository.");
      await onIssued();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not simulate delivery.");
    } finally { setBusy(false); }
  }
  return (
    <div className="rounded-xl border border-dashed p-3 flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: `${GOLD}88`, background: `${GOLD}0F` }} data-testid="demo-issued-bar">
      <div className="text-[11.5px] text-slate-600 leading-relaxed flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: NAVY }} />
        <div>
          <span className="font-semibold" style={{ color: NAVY }}>Demo · institution delivery.</span> Simulate ABC Wealth completing its normal process and delivering the final ABC Wealth Growth Portfolio contract through Altara. The document arrives with <span className="font-mono">ALTARA_ISSUED_WORKFLOW</span> provenance and links back to your original decision.
          {msg && <div className="mt-1 text-[11px] font-medium" style={{ color: NAVY }}>{msg}</div>}
        </div>
      </div>
      <button
        onClick={trigger}
        disabled={busy}
        data-testid="demo-issue-btn"
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-40 whitespace-nowrap"
        style={{ background: NAVY }}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        Simulate delivery
      </button>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    ACTIVE:      { bg: "#ECFDF5", fg: "#065F46", label: "Active" },
    EXPIRED:     { bg: "#FEE2E2", fg: "#991B1B", label: "Expired" },
    MATURED:     { bg: "#F1F5F9", fg: "#334155", label: "Matured" },
    CANCELLED:   { bg: "#FEE2E2", fg: "#991B1B", label: "Cancelled" },
    REPLACED:    { bg: "#F1F5F9", fg: "#334155", label: "Replaced" },
    ON_FILE:     { bg: "#F1F5F9", fg: "#334155", label: "On file" },
  };
  const m = map[status] || { bg: "#F1F5F9", fg: "#94A3B8", label: "Status unknown" };
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold" style={{ background: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}

function formatDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}

/* ─────────────────── detail drawer ─────────────────── */
function DocumentDetailDrawer({ docId, deviceId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [explain, setExplain] = useState(null);
  const [tab, setTab] = useState("overview"); // overview | ask | explain
  const [asking, setAsking] = useState(false);
  const [q, setQ] = useState("");
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    let c = false;
    (async () => {
      const { data } = await axios.get(`${API}/wallet/financial-documents/${docId}`, { params: { device_id: deviceId } });
      if (!c) setDoc(data);
    })();
    return () => { c = true; };
  }, [docId, deviceId]);

  async function runExplain() {
    const { data } = await axios.get(`${API}/wallet/financial-documents/${docId}/explain`, { params: { device_id: deviceId } });
    setExplain(data);
    setTab("explain");
  }
  async function askQuestion(question) {
    if (!question || asking) return;
    setAsking(true);
    try {
      const { data } = await axios.post(`${API}/wallet/financial-documents/${docId}/ask`, { device_id: deviceId, question });
      setAnswers(a => [{ question, ...data }, ...a]);
      setQ("");
    } finally { setAsking(false); }
  }

  const SUGGESTED = [
    "What am I covered for?",
    "What am I paying?",
    "Who are the beneficiaries?",
    "What are the important exclusions?",
    "Are there any waiting periods?",
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="document-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-white/95 backdrop-blur">
          <button onClick={onClose} className="inline-flex items-center gap-1 text-[12px] font-medium text-slate-500" data-testid="drawer-close">
            <ArrowLeft className="w-3.5 h-3.5" /> Close
          </button>
          {doc && <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>{doc.category_label}</div>}
        </div>

        {!doc ? (
          <div className="p-8 text-slate-400">Loading…</div>
        ) : (
          <div className="p-5 lg:p-6 space-y-5">
            <div>
              <h2 className="font-display text-2xl font-semibold" style={{ color: NAVY }}>{doc.document_name}</h2>
              <div className="text-[12px] text-slate-600 mt-1">
                {doc.document_type} · {doc.issuer_name || "Unknown issuer"} · {formatDate(doc.issue_date)}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <StatusPill status={doc.status} />
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold" style={{ background: `${GOLD}22`, color: NAVY }}>
                  {sourceLabel(doc.source_type)}
                </span>
                {doc.institution_id && (
                  <Link to={`/wallet/institutions/${doc.institution_id}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold" style={{ background: "#ECFDF5", color: "#065F46" }} data-testid="drawer-view-institution">
                    <Building2 className="w-3 h-3" /> View institution
                  </Link>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
              {[
                { k: "overview", label: "Overview" },
                { k: "explain",  label: "Explain this document" },
                { k: "ask",      label: "Ask NAVI" },
              ].map(t => (
                <button
                  key={t.k}
                  onClick={() => { setTab(t.k); if (t.k === "explain" && !explain) runExplain(); }}
                  data-testid={`drawer-tab-${t.k}`}
                  className="rounded-full px-3 py-1.5 text-[11.5px] font-semibold"
                  style={{
                    background: tab === t.k ? NAVY : "transparent",
                    color:      tab === t.k ? "white" : NAVY,
                  }}
                >{t.label}</button>
              ))}
            </div>

            {tab === "overview" && (
              <div className="space-y-3" data-testid="drawer-overview">
                <div className="rounded-xl border border-slate-100 p-4">
                  <div className="text-[10.5px] uppercase tracking-widest font-semibold mb-2" style={{ color: GOLD }}>Key information</div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[12.5px]">
                    <MiniRow k="Reference" v={doc.policy_reference || "Not identified"} />
                    <MiniRow k="Effective date" v={formatDate(doc.effective_date)} />
                    {doc.attributes.map(a => (
                      <MiniRow key={a.attribute_id} k={a.label} v={a.value} sub={a.source_location} />
                    ))}
                  </dl>
                </div>
              </div>
            )}

            {tab === "explain" && (
              <div className="space-y-3" data-testid="drawer-explain">
                {!explain ? (
                  <div className="text-slate-400 text-[12px]"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> NAVI is reading this document…</div>
                ) : (
                  <>
                    <div className="rounded-xl p-4" style={{ background: `${GOLD}12`, border: `1px solid ${GOLD}44` }}>
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: NAVY }} />
                        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{explain.headline}</div>
                      </div>
                    </div>
                    {explain.sections.map((s, i) => (
                      <div key={i} className="rounded-xl border border-slate-100 p-3">
                        <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>{s.title}</div>
                        <div className="text-[12.5px] mt-0.5 leading-relaxed" style={{ color: NAVY }}>{s.body}</div>
                      </div>
                    ))}
                    <div className="text-[10.5px] text-slate-500 flex items-start gap-1"><Info className="w-3 h-3 mt-0.5 flex-shrink-0" /> {explain.boundary}</div>
                  </>
                )}
              </div>
            )}

            {tab === "ask" && (
              <div className="space-y-3" data-testid="drawer-ask">
                <div className="rounded-xl border border-slate-100 p-4">
                  <div className="text-[10.5px] uppercase tracking-widest font-semibold mb-2" style={{ color: GOLD }}>Suggested questions</div>
                  <div className="flex flex-wrap gap-1.5">
                    {SUGGESTED.map(s => (
                      <button
                        key={s}
                        onClick={() => askQuestion(s)}
                        data-testid={`ask-suggested-${s.slice(0,20).replace(/[^a-z]/gi,"-")}`}
                        className="rounded-full px-3 py-1 text-[11.5px] font-medium border border-slate-200 hover:bg-slate-50"
                        style={{ color: NAVY }}
                      >{s}</button>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      value={q}
                      onChange={e => setQ(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && askQuestion(q)}
                      placeholder="Ask NAVI about this document…"
                      data-testid="ask-input"
                      className="flex-1 rounded-full border border-slate-200 px-3 py-2 text-[12.5px] focus:outline-none focus:ring-2"
                    />
                    <button onClick={() => askQuestion(q)} disabled={asking || !q} data-testid="ask-submit" className="rounded-full px-4 py-2 text-[12.5px] font-semibold text-white disabled:opacity-40" style={{ background: NAVY }}>
                      Ask
                    </button>
                  </div>
                </div>

                {answers.map((a, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 p-3" data-testid={`ask-answer-${i}`}>
                    <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-500">You asked</div>
                    <div className="text-[12.5px] mt-0.5" style={{ color: NAVY }}>"{a.question}"</div>
                    <div className="text-[10.5px] uppercase tracking-widest font-semibold mt-2" style={{ color: GOLD }}>NAVI</div>
                    <div className="text-[13px] mt-0.5" style={{ color: NAVY }}>{a.answer}</div>
                    {a.grounded_in && (
                      <div className="text-[10.5px] text-slate-500 mt-1">Found in: {a.grounded_in.label} · {a.grounded_in.source_location}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniRow({ k, v, sub }) {
  return (
    <div>
      <dt className="text-[10.5px] uppercase tracking-widest text-slate-400">{k}</dt>
      <dd className="font-medium" style={{ color: NAVY }}>{v}</dd>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function sourceLabel(s) {
  switch (s) {
    case "CONSUMER_UPLOAD":         return "Uploaded by you";
    case "PARTICIPATING_INSTITUTION": return "Received through Altara";
    case "ALTARA_ISSUED_WORKFLOW":  return "Received through Altara";
    case "DEMO":                    return "Demo record";
    default:                        return s;
  }
}

/* ─────────────────── upload drawer ─────────────────── */
function UploadDrawer({ deviceId, onClose, onSaved }) {
  const [phase, setPhase] = useState("pick");   // pick | extracting | confirm | saving
  const [file, setFile]   = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [editing, setEditing] = useState(false);

  async function startUpload() {
    if (!file) return;
    setPhase("extracting");
    const { data } = await axios.post(`${API}/wallet/financial-documents/upload`, {
      device_id: deviceId,
      file_name: file.name,
      category_hint: null,
    });
    setExtraction(data);
    setPhase("confirm");
  }
  async function saveConfirmed() {
    setPhase("saving");
    await axios.post(`${API}/wallet/financial-documents/confirm`, {
      device_id: deviceId,
      extraction,
    });
    onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="upload-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-white/95 backdrop-blur">
          <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>Add document</div>
          <button onClick={onClose} className="text-slate-400"><X className="w-4 h-4" /></button>
        </div>

        {phase === "pick" && (
          <div className="p-6 space-y-4" data-testid="upload-pick">
            <p className="text-[13px] text-slate-600 leading-relaxed">
              Upload an existing financial document and NAVI will help organise it. Supported: PDF, JPG, PNG.
            </p>
            <label className="block rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer hover:bg-slate-50" style={{ borderColor: "#CBD5E1" }}>
              <Upload className="w-8 h-8 mx-auto text-slate-400" />
              <div className="mt-2 text-[13px] font-semibold" style={{ color: NAVY }}>{file ? file.name : "Choose a file"}</div>
              <div className="text-[11px] text-slate-500 mt-1">PDF, JPG or PNG · up to 20 MB</div>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files?.[0])} data-testid="upload-file-input" className="hidden" />
            </label>
            <button
              onClick={startUpload}
              disabled={!file}
              data-testid="upload-start"
              className="w-full rounded-full py-3 text-[13px] font-semibold text-white disabled:opacity-40"
              style={{ background: NAVY }}
            >
              Upload and organise
            </button>
          </div>
        )}

        {phase === "extracting" && (
          <div className="p-8 text-center" data-testid="upload-extracting">
            <Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: NAVY }} />
            <div className="mt-3 font-display font-semibold" style={{ color: NAVY }}>NAVI is reading your document…</div>
            <div className="text-[11.5px] text-slate-500 mt-1">Extracting document type, issuer and key values.</div>
          </div>
        )}

        {phase === "confirm" && extraction && (
          <div className="p-5 space-y-4" data-testid="upload-confirm">
            <div className="rounded-xl p-4" style={{ background: `${GOLD}12`, border: `1px solid ${GOLD}44` }}>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: NAVY }} />
                <div className="font-semibold text-[13.5px]" style={{ color: NAVY }}>NAVI organised your document</div>
              </div>
              <div className="text-[12px] text-slate-600 mt-1">Here's what I found. Check that everything looks right before I add it to your Financial Documents.</div>
            </div>

            <div className="rounded-xl border border-slate-100 divide-y">
              {Object.entries(extraction.fields).map(([k, v]) => (
                <ExtractedRow
                  key={k}
                  field={k}
                  data={v}
                  editing={editing}
                  onChange={val => setExtraction(e => ({ ...e, fields: { ...e.fields, [k]: { ...v, value: val } } }))}
                />
              ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={saveConfirmed} data-testid="upload-confirm-save" className="flex-1 rounded-full py-3 text-[13px] font-semibold text-white inline-flex items-center justify-center gap-1.5" style={{ background: NAVY }}>
                <CheckCircle2 className="w-4 h-4" /> Confirm and save
              </button>
              <button onClick={() => setEditing(v => !v)} data-testid="upload-confirm-edit" className="rounded-full py-3 px-4 text-[12.5px] font-semibold border border-slate-200" style={{ color: NAVY }}>
                <Edit3 className="w-4 h-4 inline mr-1" /> {editing ? "Done" : "Edit details"}
              </button>
            </div>
            <div className="text-[10.5px] text-slate-500 flex items-start gap-1"><Info className="w-3 h-3 mt-0.5 flex-shrink-0" /> {extraction.boundary}</div>
          </div>
        )}

        {phase === "saving" && (
          <div className="p-8 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" style={{ color: NAVY }} /><div className="mt-3 text-slate-600">Saving…</div></div>
        )}
      </div>
    </div>
  );
}

function ExtractedRow({ field, data, editing, onChange }) {
  const label = field.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        {editing ? (
          <input value={data.value} onChange={e => onChange(e.target.value)} className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1 text-[12.5px]" style={{ color: NAVY }} />
        ) : (
          <div className="text-[13px] font-medium" style={{ color: NAVY }}>{data.value}</div>
        )}
        {data.source_location && <div className="text-[10px] text-slate-400 mt-0.5">Found: {data.source_location}</div>}
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Confidence</div>
        <div className="text-[11px] font-mono font-semibold" style={{ color: data.confidence >= 0.9 ? "#059669" : "#B45309" }}>
          {Math.round((data.confidence || 0) * 100)}%
        </div>
      </div>
    </div>
  );
}
